// dexie.ts
import { dexieAbsenceRequest } from "@/entities/absenceRequest/absenceRequest"
import Dexie, { DBCoreAddRequest, DBCorePutRequest } from "dexie"
import { dexieAttendanceEvent } from "../entities/attendanceEvent/attendanceEventMeta"
import { dexieUser } from "../entities/user/user"
import { dexieSchoolYear } from "@/entities/schoolYear/schoolYear"
import { dexieGlobalSettings } from "@/entities/globalSettings/globalSettings"
import { localDatabaseEncryptionKey } from "@/config/env"
import { DocumentData } from "firebase/firestore"
import sjcl from "browserify-sjcl"

const allDexieIndexes = [
   ...dexieUser.split(","),
   ...dexieAttendanceEvent.split(","),
   ...dexieAbsenceRequest.split(","),
   ...dexieSchoolYear.split(","),
   ...dexieGlobalSettings.split(","),
   "from",
   "to",
   "start_date",
   "end_date",
   "created_at",
   "updated_at",
]
   .map((s) => s.trim())
   .filter((v, i, a) => a.indexOf(v) === i) // remove duplicates

const encrypt = (obj: DocumentData) => {
   if (obj === undefined || obj === null) {
      return obj
   }
   const encryptedObj: any = {}
   Object.keys(obj).forEach((key) => {
      if (!allDexieIndexes.includes(key) && obj[key] !== undefined && obj[key] !== null) {
         encryptedObj[key] = sjcl.encrypt(localDatabaseEncryptionKey, JSON.stringify(obj[key]))
      } else {
         encryptedObj[key] = obj[key]
      }
   })
   return encryptedObj
}

const decrypt = (obj: DocumentData) => {
   if (obj === undefined || obj === null) {
      return obj
   }
   const decryptedObj: any = {}
   Object.keys(obj).forEach((key) => {
      if (!allDexieIndexes.includes(key) && obj[key] !== undefined && obj[key] !== null) {
         try {
            decryptedObj[key] = JSON.parse(sjcl.decrypt(localDatabaseEncryptionKey, obj[key]))
         } catch (error) {
            console.warn(`Failed to decrypt field '${key}'`, error)
            decryptedObj[key] = obj[key]
         }
      } else {
         decryptedObj[key] = obj[key]
      }
   })
   return decryptedObj
}

// Add logging middleware
export const middleware = (dexie: Dexie) =>
   dexie.use({
      stack: "dbcore",
      name: "EncryptionMiddleware",
      create(downlevelDatabase) {
         return {
            ...downlevelDatabase,
            table(tableName) {
               const downlevelTable = downlevelDatabase.table(tableName)
               return {
                  ...downlevelTable,
                  mutate: (req) => {
                     //  logger(`[DB Write] Table: ${tableName}`, {
                     //     type: req.type,
                     //     values: req.values,
                     //  })
                     if (["add", "put"].includes(req.type)) {
                        const reqq = req as DBCoreAddRequest | DBCorePutRequest
                        reqq.values = (reqq.values as any).map(encrypt)
                     }
                     return downlevelTable.mutate(req).then((res) => {
                        return res
                     })
                  },
                  get: async (req) => {
                     // logger(`[DB Read] Table: ${tableName}`, { query: req })
                     let result = await downlevelTable.get(req)
                     // logger(`[DB Read] Table: ${tableName} => encrypted result`, {
                     //     query: req,
                     //     result: result,
                     //  })

                     result = decrypt(result)
                     // logger(`[DB Read] Table: ${tableName} => decrypted result`, {
                     //     query: req,
                     //     result: result,
                     //  })
                     return result
                  },
                  getMany: async (req) => {
                     // logger(`[DB Read Many] Table: ${tableName}`, { queries: req })
                     const results = await downlevelTable.getMany(req)
                     return results.map(decrypt)
                  },
                  query: (req) => {
                     // logger(`[DB Query] Table: ${tableName}`, {
                     //   query: req,
                     //   limit: req.limit,
                     //   offset: req.offset,
                     // })
                     return downlevelTable.query(req).then((result) => ({
                        ...result,
                        result: result.result.map(decrypt),
                     }))
                  },
                  openCursor: async (req) => {
                     // logger(`[DB Open Cursor] Table: ${tableName}`, { query: req })
                     const cursor = await downlevelTable.openCursor(req)
                     // logger(`[DB Open Cursor] Table: ${tableName} => result`, { result: cursor })
                     if (!cursor) {
                        return cursor
                     }

                     return Object.create(cursor, {
                        continue: {
                           get() {
                              return cursor.continue
                           },
                        },
                        continuePrimaryKey: {
                           get() {
                              return cursor.continuePrimaryKey
                           },
                        },
                        key: {
                           get() {
                              return cursor.key
                           },
                        },
                        value: {
                           get() {
                              return decrypt(cursor.value)
                           },
                        },
                     })
                  },
               }
            },
         }
      },
   })
